import { Attachment, ErrorMessages, Icon, Label } from "@/components"
import { useStyles } from "@/components/AttachmentsFormSection/BaseAttachmentsFormSection.styles"
import { Button, FileButton, Stack, Text, useMantineTheme } from "@mantine/core"
import React from "react"
import { FieldError, UseFormRegisterReturn } from "react-hook-form"

export interface AttachmentFile {
    url?: string
    id: number
    type?: string
    name: string
    size?: number
}

interface Props<T extends boolean | undefined> {
    attachments: AttachmentFile[]
    buttonText?: string
    label?: string
    /* controls loading state */
    isUploading: boolean
    /* set to true to allow multiple files to be uploaded */
    multiple?: T
    onFileButtonChange: (arg0: T extends true ? File[] : File) => Promise<void> | void
    onDeleteFile: (attachment: AttachmentFile) => void
    /* return of the react hook form register, e.g. formContext.register("my_field") */
    registerReturn: UseFormRegisterReturn<string>
    tooltip?: string
    accept?: string
    error?: FieldError
}

export const BaseAttachmentsFormSection = <T extends boolean | undefined>({
    onFileButtonChange,
    multiple,
    isUploading,
    buttonText = "Attach a file",
    attachments,
    onDeleteFile,
    registerReturn,
    label,
    tooltip = "",
    error,
    accept = "",
}: Props<T>) => {
    const { classes } = useStyles()
    const theme = useMantineTheme()
    return (
        <div className={classes.container}>
            {label && (
                <div>
                    <Label label={label} tooltip={tooltip} />
                </div>
            )}
            {/* react-hook-form needs an input to register a field in the formstate, so we'll just hide it here */}
            <input {...registerReturn} hidden />
            <div className={classes.wrapper}>
                <FileButton onChange={onFileButtonChange} multiple={multiple} accept={accept}>
                    {(props) => (
                        <Button
                            leftIcon={<Icon icon="paperclip" />}
                            size="xs"
                            loading={isUploading}
                            aria-disabled={isUploading}
                            {...props}
                        >
                            {isUploading ? "Uploading" : buttonText}
                        </Button>
                    )}
                </FileButton>
                {error?.type && <ErrorMessages fieldError={error} />}
                {attachments?.length > 0 && (
                    <Stack className={classes.attachments} spacing={4}>
                        <Text aria-atomic="true" aria-live="polite">
                            <Icon icon="check-circle" color={theme.colors.green[5]} /> {attachments.length}&nbsp;
                            {attachments.length > 1 ? "files" : "file"} attached successfully
                        </Text>
                        {attachments?.map((attachment) => (
                            <Attachment
                                key={attachment.id}
                                disabled={isUploading}
                                fileName={attachment.name}
                                fileExtension={attachment.name.split(".").pop()}
                                onDelete={() => onDeleteFile(attachment)}
                            />
                        ))}
                    </Stack>
                )}
            </div>
        </div>
    )
}
